import * as React from 'react';

import { FieldRenderProps, FieldWrapper } from '@progress/kendo-react-form';
import { filterBy } from '@progress/kendo-data-query';
import {
    Input, MaskedTextBox, NumericTextBox,
    Checkbox, ColorPicker, Switch, RadioGroup,
    Slider, SliderLabel, RangeSlider, TextArea, Rating
} from '@progress/kendo-react-inputs';
import {
    DatePicker, TimePicker, DateTimePicker,
    DateRangePicker, DateInput, Calendar, CalendarProps
} from '@progress/kendo-react-dateinputs';
import { Label, Error, Hint, FloatingLabel } from '@progress/kendo-react-labels';
import { Upload } from '@progress/kendo-react-upload';
import { DropDownList, AutoComplete, MultiSelect, ComboBox, MultiColumnComboBox, DropDownTree } from '@progress/kendo-react-dropdowns';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { expandedState, processTreeData } from './TreeDataOperations';
import { ReactMultiEmail } from 'react-multi-email';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../messages/en-US';


interface columnsInterface {
  field: string,
  header: any,
  width: string
}

// Disable Fast Navigational Bar until this critical bug is fixed by Telerik (https://github.com/telerik/kendo-react/issues/1566)
const CustomCalendar = (props: CalendarProps) => {
  return <Calendar {...props} navigation={false} />
};

export const FormInput = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, type, optional, validator, validatorRequired, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';

    return (
      <FieldWrapper>
        <Label editorId={id} editorValid={valid} editorDisabled={disabled} optional={optional}>{label}</Label>
        <div className={'k-form-field-wrap'}>
          <Input
            valid={valid}
            type={type}
            id={id}
            disabled={disabled}
            ariaDescribedBy={`${hintId} ${errorId}`}
            {...others}
                />
          {
                    showHint &&
                    <Hint id={hintId}>{hint}</Hint>
                }
          {
                    showValidationMessage &&
                    <Error id={errorId}>{validationMessage}</Error>
                }
        </div>
      </FieldWrapper>
    );
};


export const FormRadioGroup = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, id, label, valid, disabled, hint, visited, modified, validator, validatorRequired, ...others } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper>
        <Label id={labelId} editorRef={editorRef} editorId={id} editorValid={valid} editorDisabled={disabled}>{label}</Label>
        <RadioGroup
          ariaDescribedBy={`${hintId} ${errorId}`}
          ariaLabelledBy={labelId}
          valid={valid}
          disabled={disabled}
          ref={editorRef}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormNumericTextBox = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, validator, validatorRequired, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';

    return (
      <FieldWrapper>
        <Label editorId={id} editorValid={valid} editorDisabled={disabled}>{label}</Label>
        <NumericTextBox
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormCheckbox = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, id, valid, disabled, hint, optional, label, visited, modified, validator, validatorRequired, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';

    return (
      <FieldWrapper>
        <Checkbox
          ariaDescribedBy={`${hintId} ${errorId}`}
          label={label}
          labelOptional={optional}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormSwitch = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, optional, id, valid, disabled, hint, validator, validatorRequired, ...others } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper>
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
          optional={optional}
            >
          {label}
        </Label>
        <Switch
          ref={editorRef}
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormButtonGroup = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, optional, id, valid, disabled, hint, value, onChange, enabledLabel, disabledLabel } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);
    const localizationService = useLocalization();

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    const isEnabled = value === true;
    const enabledText = enabledLabel || localizationService.toLanguageString('custom.enabled', enMessages.custom.enabled);
    const disabledText = disabledLabel || 'Disabled';

    return (
      <FieldWrapper>
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
          optional={optional}
            >
          {label}
        </Label>
        <ButtonGroup>
          <Button
            togglable={true}
            selected={isEnabled}
            onClick={(e) => {
              e.preventDefault();
              onChange({ value: true });
            }}
          >
            {enabledText}
          </Button>
          <Button
            togglable={true}
            selected={!isEnabled}
            onClick={(e) => {
              e.preventDefault();
              onChange({ value: false });
            }}
          >
            {disabledText}
          </Button>
        </ButtonGroup>
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormSegmentedOptions = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, optional, id, valid, disabled, hint, value, onChange, data = [] } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint;
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    const selectedValue = value?.value ?? value;

    return (
      <FieldWrapper>
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
          optional={optional}
        >
          {label}
        </Label>
        <ButtonGroup>
          {data.map((item: { text: string; value: any }) => (
            <Button
              key={item.value}
              togglable={true}
              selected={selectedValue === item.value}
              disabled={disabled}
              onClick={(e) => {
                e.preventDefault();
                onChange({ value: item });
              }}
            >
              {item.text}
            </Button>
          ))}
        </ButtonGroup>
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
};

export const FormMaskedTextBox = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, hint, optional, validator, validatorRequired, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';

    return (
      <FieldWrapper>
        <Label editorId={id} editorValid={valid} optional={optional}>{label}</Label>
        <div className={'k-form-field-wrap'}>
          <MaskedTextBox
            ariaDescribedBy={`${hintId} ${errorId}`}
            valid={valid}
            id={id}
            {...others}
                />
          {
                    showHint &&
                    <Hint id={hintId}>{hint}</Hint>
                }
          {
                    showValidationMessage &&
                    <Error id={errorId}>{validationMessage}</Error>
                }
        </div>
      </FieldWrapper>
    );
};

export const FormTextArea = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, hint, disabled, optional, wrapperStyle, validator, validatorRequired, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';

    return (
      <FieldWrapper style={wrapperStyle}>
        {label ? (
          <Label editorId={id} editorValid={valid} optional={optional}>{label}</Label>
        ) : null}
        <TextArea
          valid={valid}
          id={id}
          disabled={disabled}
          ariaDescribedBy={`${hintId} ${errorId}`}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormColorPicker = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, wrapperStyle, validator, validatorRequired, ...others } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
            >
          {label}
        </Label>
        <ColorPicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormSlider = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, data, min, max, validator, validatorRequired, ...others } = fieldRenderProps;

    const editorRef = React.useRef<any>(null);
    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper>
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
            >
          {label}
        </Label>
        <Slider
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={id}
          disabled={disabled}
          min={min}
          max={max}
          {...others}
            >
          {
                    data.map(value => (
                      <SliderLabel
                        title={value}
                        key={value}
                        position={value}
                        >
                        {value.toString()}
                      </SliderLabel>
                    ))
                }
        </Slider>
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormRangeSlider = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, hint, disabled, data, min, max, validator, validatorRequired, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';

    return (
      <FieldWrapper>
        <Label editorId={id} editorValid={valid}>{label}</Label>
        <RangeSlider
          valid={valid}
          id={id}
          disabled={disabled}
          ariaDescribedBy={`${hintId} ${errorId}`}
          min={min}
          max={max}
          {...others}
            >{
                data.map(value => {
                    return  (
                      <SliderLabel
                        key={value}
                        position={value}
                    >
                        {value.toString()}
                      </SliderLabel>
                )
})
            }
        </RangeSlider>
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormRating = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, hint, disabled, optional, validator, validatorRequired, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';

    return (
      <FieldWrapper>
        <Label editorId={id} editorValid={valid} optional={optional}>{label}</Label>
        <Rating
          valid={valid}
          id={id}
          disabled={disabled}
          ariaDescribedBy={`${hintId} ${errorId}`}
          {...others}
            />
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormUpload = (fieldRenderProps: FieldRenderProps) => {
    const { value, id, optional, label, hint, validationMessage, touched, validator, validatorRequired, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    const onChangeHandler = (event: any) => {
        fieldRenderProps.onChange({ value: event.newState });
    };
    const onRemoveHandler = (event: any) => {
        fieldRenderProps.onChange({ value: event.newState });
    };

    return (
      <FieldWrapper>
        <Label id={labelId} editorId={id} optional={optional}>
          {label}
        </Label>
        <Upload
          id={id}
          autoUpload={false}
          showActionButtons={false}
          multiple={false}
          files={value}
          onAdd={onChangeHandler}
          onRemove={onRemoveHandler}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ariaLabelledBy={labelId}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormDropDownList = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, wrapperStyle, validator, validatorRequired, ...others } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
            >
          {label}
        </Label>
        <DropDownList
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormAutoComplete = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, wrapperStyle, validator, validatorRequired, ...others } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
            >
          {label}
        </Label>
        <AutoComplete
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

/** Single-select ComboBox with searchable data and explicit "Add ..." item for custom values. */
export const FormComboBoxWithCustom = (fieldRenderProps: FieldRenderProps) => {
    const {
        validationMessage,
        touched,
        label,
        id,
        valid,
        disabled,
        hint,
        wrapperStyle,
        value,
        onChange,
        data = [],
        dataItemKey = 'value',
        textField = 'text',
        onCustomValueAdded,
        ...others
    } = fieldRenderProps;
    const ADD_NEW_ITEM_FLAG = '__isAddNew__';
    const CUSTOM_TEXT_FIELD = '__customText__';

    const sortedData = React.useMemo(() => {
        const arr = Array.isArray(data) ? data.slice() : [];
        return arr.sort((a, b) => {
            const aText = a?.[textField] ?? '';
            const bText = b?.[textField] ?? '';
            return String(aText).localeCompare(String(bText));
        });
    }, [data, textField]);

    const [filteredData, setFilteredData] = React.useState(sortedData);
    const [filterText, setFilterText] = React.useState('');

    React.useEffect(() => {
        setFilteredData(sortedData);
    }, [sortedData]);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint;
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    const toCustomItem = (rawValue: any) => {
        const customVal = typeof rawValue === 'string'
            ? rawValue.trim()
            : rawValue?.[CUSTOM_TEXT_FIELD] ?? rawValue?.[textField] ?? String(rawValue ?? '').trim();

        if (!customVal) {
            return null;
        }

        return { [textField]: customVal, [dataItemKey]: customVal };
    };

    const handleChange = (e: any) => {
        let nextValue = e.target?.value;

        if (nextValue != null && typeof nextValue === 'object' && nextValue[ADD_NEW_ITEM_FLAG]) {
            const newItem = toCustomItem(nextValue);
            if (newItem) {
                nextValue = newItem;
                onCustomValueAdded?.(newItem);
            }
        } else {
            const isExistingItem = nextValue != null && typeof nextValue === 'object' && nextValue[dataItemKey] !== undefined;
            if (nextValue != null && !isExistingItem) {
                const newItem = toCustomItem(nextValue);
                if (newItem) {
                    nextValue = newItem;
                    onCustomValueAdded?.(newItem);
                }
            }
        }

        setFilterText('');
        setFilteredData(sortedData);
        onChange?.({
            ...e,
            value: nextValue,
            target: { ...e.target, value: nextValue },
        });
    };

    const handleFilterChange = (e: any) => {
        const nextFilterText = String(e.filter?.value ?? '').trim();
        setFilterText(nextFilterText);
        setFilteredData(filterBy(sortedData, e.filter));
    };

    const displayData = React.useMemo(() => {
        if (!filterText) {
            return filteredData;
        }

        const normalizedFilter = filterText.toLowerCase();
        const hasExactMatch = sortedData.some((item) => {
            const itemText = String(item?.[textField] ?? '').trim().toLowerCase();
            const itemValue = String(item?.[dataItemKey] ?? '').trim().toLowerCase();
            return itemText === normalizedFilter || itemValue === normalizedFilter;
        });

        if (hasExactMatch) {
            return filteredData;
        }

        return [
            {
                [textField]: `Add "${filterText}"`,
                [dataItemKey]: filterText,
                [ADD_NEW_ITEM_FLAG]: true,
                [CUSTOM_TEXT_FIELD]: filterText,
            },
            ...filteredData,
        ];
    }, [dataItemKey, filterText, filteredData, sortedData, textField]);

    const normalizedValue = React.useMemo(() => {
        if (value == null || value === '') {
            return null;
        }

        if (typeof value === 'object') {
            const existingText = value?.[ADD_NEW_ITEM_FLAG]
                ? value?.[CUSTOM_TEXT_FIELD]
                : value?.[textField];
            const existingKey = value?.[dataItemKey];

            return {
                ...value,
                [textField]: existingText ?? String(existingKey ?? ''),
                [dataItemKey]: existingKey ?? existingText ?? '',
            };
        }

        return { [textField]: String(value), [dataItemKey]: value };
    }, [dataItemKey, textField, value]);

    const handleItemRender = (li: React.ReactElement, itemProps: any) => {
        const dataItem = itemProps.dataItem;

        if (!dataItem?.[ADD_NEW_ITEM_FLAG]) {
            return li;
        }

        return React.cloneElement(
            li,
            li.props,
            <span className="flex items-center justify-between w-full font-medium">
                <span>{dataItem[textField]}</span>
                <span className="text-lg leading-none">+</span>
            </span>
        );
    };

    return (
        <FieldWrapper style={wrapperStyle}>
            <Label
                id={labelId}
                editorId={id}
                editorValid={valid}
                editorDisabled={disabled}
            >
                {label}
            </Label>
            <ComboBox
                ariaLabelledBy={labelId}
                ariaDescribedBy={`${hintId} ${errorId}`}
                valid={valid}
                id={id}
                disabled={disabled}
                data={displayData}
                value={normalizedValue}
                onChange={handleChange}
                onFilterChange={handleFilterChange}
                itemRender={handleItemRender}
                allowCustom={true}
                filterable={true}
                dataItemKey={dataItemKey}
                textField={textField}
                suggest={false}
                placeholder='Select an option or type to add a new one'
                {...others}
            />
            {showHint && <Hint id={hintId}>{hint}</Hint>}
            {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
        </FieldWrapper>
    );
};

export const FormComboBox = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, wrapperStyle, validator, validatorRequired, ...others } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label id={labelId} editorRef={editorRef} editorId={id} editorValid={valid} editorDisabled={disabled}>
          {label}
        </Label>
        <ComboBox
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          placeholder='Select an option or type to add a new one'
          id={id}
          disabled={disabled}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormMultiColumnComboBox = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, wrapperStyle, validator, validatorRequired, ...others } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    const columns: Array<columnsInterface> = [
        { field: 'id', header: <span>header</span>, width: '100px' },
        { field: 'name', header: 'Name', width: '300px' },
        { field: 'position', header: 'Position', width: '300px' }
    ];

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label id={labelId} editorRef={editorRef} editorId={id} editorValid={valid} editorDisabled={disabled}>
          {label}
        </Label>
        <MultiColumnComboBox
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={id}
          placeholder='Select streams'
          disabled={disabled}
          columns={columns}
          textField={'name'}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormMultiSelect = (fieldRenderProps: FieldRenderProps) => {
    const {
        validationMessage,
        touched,
        label,
        id,
        valid,
        disabled,
        hint,
        wrapperStyle,
        value,
        onChange,
        data = [],
        dataItemKey = 'value',
        textField = 'text',
        onCustomValueAdded,
        allowCustom = false,
        filterable = false,
        validator,
        validatorRequired,
        ...others
    } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);
    const showValidationMessage = touched && validationMessage;
    const showHint = !showValidationMessage && hint;
    const hintId = showHint ? `${id}_hint` : "";
    const errorId = showValidationMessage ? `${id}_error` : "";
    const labelId = label ? `${id}_label` : "";
    const [filter, setFilter] = React.useState<any>(null);
    // Kendo only filters when the parent handles onFilterChange.
    const displayData = React.useMemo(
        () => (filterable && filter?.value ? filterBy(data, filter) : data),
        [data, filter, filterable]
    );

    const normalizedValue = React.useMemo(() => {
        if (!Array.isArray(value)) {
            return [];
        }

        return value
            .map((item) => {
                if (item == null) {
                    return null;
                }

                if (typeof item === 'string') {
                    return { [textField]: item, [dataItemKey]: item };
                }

                if (typeof item === 'object') {
                    const existingText = item?.[textField] ?? item?.[dataItemKey];
                    const existingKey = item?.[dataItemKey] ?? existingText;

                    return {
                        ...item,
                        [textField]: existingText ?? '',
                        [dataItemKey]: existingKey ?? '',
                    };
                }

                return null;
            })
            .filter(Boolean);
    }, [dataItemKey, textField, value]);

    const handleChange = (event: any) => {
        const nextValue = Array.isArray(event.value) ? event.value : [];
        const normalizedItems = nextValue
            .map((item) => {
                if (typeof item === 'string') {
                    const normalizedItem = { [textField]: item, [dataItemKey]: item };
                    onCustomValueAdded?.(normalizedItem);
                    return normalizedItem;
                }

                if (item && typeof item === 'object') {
                    const existingText = item?.[textField] ?? item?.[dataItemKey];
                    const existingKey = item?.[dataItemKey] ?? existingText;
                    const normalizedItem = {
                        ...item,
                        [textField]: existingText ?? '',
                        [dataItemKey]: existingKey ?? '',
                    };

                    if (allowCustom && !data.some((dataItem: any) => dataItem?.[dataItemKey] === normalizedItem[dataItemKey])) {
                        onCustomValueAdded?.(normalizedItem);
                    }

                    return normalizedItem;
                }

                return null;
            })
            .filter(Boolean);

        setFilter(null);
        onChange({ value: normalizedItems });
    };

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
            >
          {label}
        </Label>
        <MultiSelect
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={id}
          disabled={disabled}
          data={displayData}
          value={normalizedValue}
          onChange={handleChange}
          dataItemKey={dataItemKey}
          textField={textField}
          allowCustom={allowCustom}
          filterable={filterable}
          filter={filter?.value ?? ''}
          onFilterChange={(e) => setFilter(e.filter)}
          onClose={() => setFilter(null)}
          {...others}
            />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
};

export const FormDropDownTree = (fieldRenderProps: FieldRenderProps) => {
    const {
        validationMessage,
        touched,
        label,
        id,
        valid,
        disabled,
        hint,
        wrapperStyle,
        data,
        validator,
        validatorRequired,
        ...others
    } = fieldRenderProps;
    const { value, selectField, expandField, dataItemKey, filter } = others;
    const [expanded, setExpanded] = React.useState<Array<number>>([data[0][dataItemKey]]);
    const treeData = React.useMemo(
        () => processTreeData(
            data,
            { expanded, value, filter },
            { selectField, expandField, dataItemKey, subItemsField: 'items' }
        ),
        [data, expanded, value, filter, selectField, expandField, dataItemKey]
    );
    const onExpandChange = React.useCallback(
        event => setExpanded(expandedState(event.item, dataItemKey, expanded)),
        [expanded, dataItemKey]
    );
    const editorRef = React.useRef<any>(null);
    const showValidationMessage = touched && validationMessage;
    const showHint = !showValidationMessage && hint;
    const hintId = showHint ? `${id}_hint` : "";
    const errorId = showValidationMessage ? `${id}_error` : "";
    const labelId = label ? `${id}_label` : "";

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label
          id={labelId}
          editorRef={editorRef}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
            >
          {label}
        </Label>
        <DropDownTree
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={id}
          disabled={disabled}
          data={treeData}
          onExpandChange={onExpandChange}
          dataItemKey={others.dataItemKey}
          textField={others.textField}
          {...others}
            />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
};


export const FormDatePicker = (fieldRenderProps: FieldRenderProps) => {
    const {
        validationMessage, touched, label, id, valid,
        disabled, hint, wrapperStyle, hintDirection, ...others
    } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';


    return (
      <FieldWrapper style={wrapperStyle}>
        <Label id={labelId} editorId={id} editorValid={valid} editorDisabled={disabled}>
          {label}
        </Label>
        <div className={'k-form-field-wrap'}>
          <DatePicker
            calendar={CustomCalendar}
            ariaLabelledBy={labelId}
            ariaDescribedBy={`${hintId} ${errorId}`}
            valid={valid}
            id={id}
            disabled={disabled}
            {...others}
                />
          {
                    showHint &&
                    <Hint id={hintId} direction={hintDirection}>{hint}</Hint>
                }
          {
                    showValidationMessage &&
                    <Error id={errorId}>{validationMessage}</Error>
                }
        </div>
      </FieldWrapper>
    );
};

export const FormDateTimePicker = (fieldRenderProps: FieldRenderProps) => {
    const {
        validationMessage, touched, label, id, valid, disabled, hint, wrapperStyle, value, onChange, onValueChange, ...others
    } = fieldRenderProps as FieldRenderProps & {
        onValueChange?: (value: Date | null | undefined) => void;
    };

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label id={labelId} editorId={id} editorValid={valid} editorDisabled={disabled}>
          {label}
        </Label>
        <DateTimePicker
          format={"dd/MM/yyyy HH:mm"}
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          value={value as Date | null | undefined}
          valid={valid}
          id={id}
          disabled={disabled}
          onChange={(event) => {
            onChange?.({ value: event.value });
            onValueChange?.(event.value);
          }}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormTimePicker = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, wrapperStyle, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label id={labelId} editorId={id} editorValid={valid} editorDisabled={disabled}>
          {label}
        </Label>
        <TimePicker
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormDateInput = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, wrapperStyle, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label id={labelId} editorId={id} editorValid={valid} editorDisabled={disabled}>
          {label}
        </Label>
        <DateInput
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormDateRangePicker = (fieldRenderProps: FieldRenderProps) => {
  const localizationService = useLocalization();
    const { validationMessage, touched, label, id, valid, disabled, hint, wrapperStyle, ...others } = fieldRenderProps;
    const editorRef = React.useRef<any>(null);

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';
    const labelId: string = label ? `${id}_label` : '';

    return (
      <FieldWrapper style={wrapperStyle}>
        <Label id={labelId} editorRef={editorRef} editorId={id} editorValid={valid} editorDisabled={disabled}>
          {label}
        </Label>
        <DateRangePicker
          startDateInputSettings={{label: `${localizationService.toLanguageString('custom.from', enMessages.custom.from)}`}}
          endDateInputSettings={{label:  `${localizationService.toLanguageString('custom.until', enMessages.custom.until)}`}}
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
            />
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormFloatingNumericTextBox = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, optional, value, ...others } = fieldRenderProps;

    const showValidationMessage: string | false | null = touched && validationMessage;
    const showHint: boolean = !showValidationMessage && hint
    const hintId: string = showHint ? `${id}_hint` : '';
    const errorId: string = showValidationMessage ? `${id}_error` : '';

    return (
      <FieldWrapper>
        <FloatingLabel
          optional={optional}
          editorValue={value}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
          label={label}
            >
          <NumericTextBox
            ariaDescribedBy={`${hintId} ${errorId}`}
            value={value}
            valid={valid}
            id={id}
            disabled={disabled}
            {...others}
                />
        </FloatingLabel>
        {
                showHint &&
                <Hint id={hintId}>{hint}</Hint>
            }
        {
                showValidationMessage &&
                <Error id={errorId}>{validationMessage}</Error>
            }
      </FieldWrapper>
    );
};

export const FormMultiEmail = (fieldRenderProps) => {

  const editorRef = React.useRef<any>(null);
  const { validationMessage, label, id, valid, disabled, hint, validateEmail, placeholder, onChange, emails, getLabel} = fieldRenderProps;
  const showValidationMessage: string | false | null = validationMessage;
  const showHint: boolean = !showValidationMessage && hint
  const hintId: string = showHint ? `${id}_hint` : '';
  const errorId: string = showValidationMessage ? `${id}_error` : '';
  const labelId: string = label ? `${id}_label` : '';

  return (
    <FieldWrapper>
      <Label id={labelId} editorRef={editorRef} editorId={id} editorValid={valid} editorDisabled={disabled}>
        {label}
      </Label>
      <ReactMultiEmail
        className="alert-emails"
        placeholder={placeholder}
        emails={emails}
        onChange={onChange}
        validateEmail={validateEmail}
        getLabel={getLabel}
      />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
}

export const FormImageUpload = (fieldRenderProps: FieldRenderProps) => {
  const { value, onChange, validationMessage, touched, id, valid } = fieldRenderProps;
  const [localPreview, setLocalPreview] = React.useState<string | null>(null);
  const localizationService = useLocalization();

  const showValidationMessage: string | false | null = touched && validationMessage;
  const errorId: string = showValidationMessage ? `${id}_error` : '';

  // Load preview from form value
  React.useEffect(() => {
    if (value && typeof value === 'string' && value.length > 0) {
      // If it's already base64 without data URL prefix, add it
      if (!value.startsWith('data:image')) {
        setLocalPreview(`data:image/png;base64,${value}`);
      } else {
        setLocalPreview(value);
      }
    } else {
      setLocalPreview(null);
    }
  }, [value]);

  return (
    <FieldWrapper>
      <Label editorId={id} editorValid={valid}>
        {(localizationService.toLanguageString("custom.image", "Image") || "Image") + " *"}
      </Label>
      <div className="k-form-hint" style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
        (JPG, JPEG, PNG, BMP, WEBP)
      </div>
      <div className="k-form-field-wrap">
        <Upload
          batch={false}
          multiple={false}
          defaultFiles={[]}
          withCredentials={false}
          saveUrl={'https://demos.telerik.com/service/v2/odata/upload/save'}
          removeUrl={'https://demos.telerik.com/service/v2/odata/upload/remove'}
          restrictions={{
            allowedExtensions: [".jpg", ".jpeg", ".png", ".bmp", ".webp"]
          }}
          onAdd={(event: any) => {
            const fileInfo = event.newState[0];
            if (fileInfo) {
              const file = (fileInfo as any).rawFile || (fileInfo as any).getRawFile?.() || (fileInfo as any).file;
              if (file instanceof File) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  const result = e.target?.result as string;
                  setLocalPreview(result);
                  // Store base64 string in form (without data:image prefix for backend)
                  const base64Data = result.split(',')[1]; // Remove data:image/...;base64, prefix
                  onChange({ value: base64Data });
                };
                reader.readAsDataURL(file);
              }
            }
          }}
          onRemove={(event: any) => {
            setLocalPreview(null);
            onChange({ value: '' });
          }}
        />
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </div>
      {localPreview && (
        <div className="flex p-4 mt-2">
          <img
            src={localPreview}
            alt="Template preview"
            style={{ maxWidth: '150px', objectFit: 'contain' }}
          />
        </div>
      )}
    </FieldWrapper>
  );
};